import type { IClientTimelineF2f } from "@gen/app-query";
import { ClientTimelineF2f, CoreQuery, Query } from "@gen/app-query";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import { DaysHoursFields } from "./DaysHoursFields";

interface F2FTimelinesProps {
  clientId: string;
  mode: "view" | "edit";
}

type F2FTimelineFormValues = IClientTimelineF2f;

export function F2FTimelines({ clientId, mode }: F2FTimelinesProps) {
  const isViewMode = mode === "view";

  const {
    data: adminF2fTimelinesData,
    isFetching: isAdminF2fTimelinesDataFetching,
    refetch: refetchAdminF2fTimelines,
  } = CoreQuery.useAdmin_getAdminClientF2fTimelinesQuery(clientId, {
    enabled: !isViewMode,
  });

  const {
    data: viewF2fTimelinesData,
    isFetching: isViewF2fTimelinesDataFetching,
  } = Query.useCore_getClientF2fTimelinesQuery(clientId, {
    enabled: isViewMode,
  });

  const f2fTimelinesData = isViewMode
    ? viewF2fTimelinesData
    : adminF2fTimelinesData;
  const isF2fTimelinesDataFetching = isViewMode
    ? isViewF2fTimelinesDataFetching
    : isAdminF2fTimelinesDataFetching;

  const memoValues = useMemo<F2FTimelineFormValues>(
    () => ({
      complaint_period_seconds: f2fTimelinesData?.complaint_period_seconds,
    }),
    [f2fTimelinesData],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<F2FTimelineFormValues>({
    defaultValues: memoValues,
  });

  useEffect(() => {
    reset(memoValues);
  }, [memoValues, reset]);

  // NOTE We have the handle 404 status here and reset the values
  // because the component has to be rerendered and has to present
  // the placeholder again to the user. Ideally, we would handle this
  // inside the `api` and just return an empty reponse.
  const resetF2fTimelines = async () => {
    if (isViewMode) {
      return;
    }
    try {
      await refetchAdminF2fTimelines({
        throwOnError: true,
      });
    } catch {
      reset({
        complaint_period_seconds: 0,
      });
    }
  };

  const { mutate: updateF2fTimelines, isPending: isUpdateF2fTimelinesPending } =
    CoreQuery.useAdmin_updateClientF2fTimelinesMutation(clientId, {
      onSuccess: () => {
        refetchAdminF2fTimelines();
      },
      onError: () => {
        resetF2fTimelines();
      },
      meta: {
        success: "F2F timelines updated",
      },
    });

  const onSubmit = (values: F2FTimelineFormValues) => {
    updateF2fTimelines(
      ClientTimelineF2f.fromJS({
        complaint_period_seconds: Number(values.complaint_period_seconds),
      }),
    );
  };

  const isLoading =
    isF2fTimelinesDataFetching || (!isViewMode && isUpdateF2fTimelinesPending);
  const isDisabled = !isDirty;

  return (
    <Stack spacing={5}>
      <Typography variant="body1DashedDivider">F2F</Typography>
      <Stack spacing={1.5}>
        <Typography variant="body1">Complaint Period</Typography>
        <DaysHoursFields
          fieldName="complaint_period_seconds"
          control={control}
          disabled={isViewMode}
          dataTestIdPrefix="f2f-complaint-period"
          showMinutes
        />
      </Stack>
      {!isViewMode && (
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          loading={isLoading}
          disabled={isDisabled}
          size="large"
          data-testid="save-f2f-timelines-button"
        >
          Save F2F Timelines
        </Button>
      )}
    </Stack>
  );
}
